import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {QuotationStatus} from "~/utilities/ottokart/quotationConstants";
import {
    PLUTO_QUOTATION_STATUS_APPROVED,
    PLUTO_QUOTATION_STATUS_CONVERTED_TO_CART,
    PLUTO_QUOTATION_STATUS_EXPIRED,
} from "~/utilities/ottokart/b2bQuotationWorkflow";
import type {
    AdvancedQuotation,
    AdvancedQuotationItem,
    Quotation,
    QuotationItem,
} from "~/domainModels/ottokart/quotations";
import type {ZodType} from "zod";
import {z as zod} from "zod";

const STOREFRONT_SYSTEM_ACTOR_ID = "00000000-0000-4000-8000-000000000001" as Uuid;
export const PLUTO_QUOTATION_EMPTY_FIELD = "-";

export type PlutoAddressSnapshot = {
    address: string;
    city: string;
    companyName: string;
    email: string;
    gstin: string | null;
    phoneNumber: string;
    pinCode: string;
    state: string;
};

export type PlutoAdminCommercialDetails = {
    billingDetails: PlutoAddressSnapshot;
    buyerBillTo: string;
    consigneeShipTo: string;
    contactNumber: string;
    customerReferenceDate: string;
    customerReferenceNumber: string;
    discountType: Uuid | null;
    discountValue: number | null;
    emailId: string;
    freightCharges: number | null;
    incoTerms: string;
    isBillingSameAsShipping: boolean;
    isAdvanced?: boolean;
    shippingDetails: PlutoAddressSnapshot;
    taxRate: number;
    termsAndConditions: Array<string>;
    termsOfPayment: string;
};

export type PlutoQuotationLineItem = {
    approvedPrice: string | null;
    compatibleMachines: Array<string>;
    createdAt: Iso8601DateTime;
    discountPercent: string | null;
    id: Uuid;
    isCustom: boolean;
    marketPrice: string | null;
    productCatalogueId: Uuid | null;
    productNameOverride: string | null;
    quantity: number;
    quotationId: Uuid;
    requestedPrice: string;
    revisionId: Uuid;
    skuOverride: string | null;
    sortOrder: number | null;
    status: string;
    updatedAt: Iso8601DateTime;
};

export type PlutoQuotation = {
    adminCommercialDetails: PlutoAdminCommercialDetails | null;
    adminNotes: string | null;
    convertedCartId: Uuid | null;
    createdAt: Iso8601DateTime;
    customerNotes: string | null;
    generatedBy: Uuid | null;
    id: Uuid;
    ottokartStorefrontCustomerId: Uuid;
    quoteNumber: string;
    rejectionReason: string | null;
    revisionId: Uuid;
    sharedBy: Uuid | null;
    sourceQuotationId: Uuid | null;
    status: string;
    totalAmount: string;
    updatedAt: Iso8601DateTime;
    validUntil: Iso8601DateTime | null;
};

const plutoAddressSnapshotZodValidator: ZodType<PlutoAddressSnapshot> = zod.object({
    address: zod.string(),
    city: zod.string(),
    companyName: zod.string(),
    email: zod.string(),
    gstin: zod.string().nullable(),
    phoneNumber: zod.string(),
    pinCode: zod.string(),
    state: zod.string(),
});

const plutoAdminCommercialDetailsZodValidator: ZodType<PlutoAdminCommercialDetails> = zod.object({
    billingDetails: plutoAddressSnapshotZodValidator,
    buyerBillTo: zod.string(),
    consigneeShipTo: zod.string(),
    contactNumber: zod.string(),
    customerReferenceDate: zod.string(),
    customerReferenceNumber: zod.string(),
    discountType: uuidZodValidator.nullable(),
    discountValue: zod.number().nullable(),
    emailId: zod.string(),
    freightCharges: zod.number().nullable(),
    incoTerms: zod.string(),
    isBillingSameAsShipping: zod.boolean(),
    isAdvanced: zod.boolean().optional(),
    shippingDetails: plutoAddressSnapshotZodValidator,
    taxRate: zod.number(),
    termsAndConditions: zod.array(zod.string()),
    termsOfPayment: zod.string(),
});

export const plutoQuotationLineItemZodValidator: ZodType<PlutoQuotationLineItem> = zod.object({
    approvedPrice: zod.string().nullable(),
    compatibleMachines: zod.array(zod.string()),
    createdAt: iso8601DateTimeZodValidator,
    discountPercent: zod.string().nullable(),
    id: uuidZodValidator,
    isCustom: zod.boolean(),
    marketPrice: zod.string().nullable(),
    productCatalogueId: uuidZodValidator.nullable(),
    productNameOverride: zod.string().nullable(),
    quantity: zod.number().int(),
    quotationId: uuidZodValidator,
    requestedPrice: zod.string(),
    revisionId: uuidZodValidator,
    skuOverride: zod.string().nullable(),
    sortOrder: zod.number().int().nullable(),
    status: zod.string(),
    updatedAt: iso8601DateTimeZodValidator,
});

export const plutoQuotationZodValidator: ZodType<PlutoQuotation> = zod.object({
    adminCommercialDetails: plutoAdminCommercialDetailsZodValidator.nullable(),
    adminNotes: zod.string().nullable(),
    convertedCartId: uuidZodValidator.nullable(),
    createdAt: iso8601DateTimeZodValidator,
    customerNotes: zod.string().nullable(),
    generatedBy: uuidZodValidator.nullable(),
    id: uuidZodValidator,
    ottokartStorefrontCustomerId: uuidZodValidator,
    quoteNumber: zod.string(),
    rejectionReason: zod.string().nullable(),
    revisionId: uuidZodValidator,
    sharedBy: uuidZodValidator.nullable(),
    sourceQuotationId: uuidZodValidator.nullable(),
    status: zod.string(),
    totalAmount: zod.string(),
    updatedAt: iso8601DateTimeZodValidator,
    validUntil: iso8601DateTimeZodValidator.nullable(),
});

export const plutoQuotationWithLineItemsZodValidator = zod.object({
    lineItems: zod.array(plutoQuotationLineItemZodValidator),
    quotation: plutoQuotationZodValidator,
});

export type LegacyQuotationWriteLineItem = {
    id?: Uuid;
    variantId?: Uuid | null;
    productName?: string;
    categoryName?: string | null;
    sku?: string | null;
    sortOrder?: number;
    isCustom?: boolean;
    sellingPrice: number;
    marketPrice: number;
    quantity: number;
    discount?: number | null;
    compatibleMachines?: Array<string> | null;
};

export type LegacyQuotationWritePayload = {
    quotationId?: Uuid;
    customerId: Uuid;
    status: Uuid;
    discountType?: Uuid | null;
    discountValue?: number | null;
    taxRate: number;
    billingDetailsCompanyName: string;
    billingDetailsGstin?: string | null;
    billingDetailsEmail: string;
    billingDetailsAddress: string;
    billingDetailsState: string;
    billingDetailsCity: string;
    billingDetailsPinCode: string;
    billingDetailsPhoneNumber: string;
    shippingDetailsCompanyName: string;
    shippingDetailsGstin?: string | null;
    shippingDetailsEmail: string;
    shippingDetailsAddress: string;
    shippingDetailsState: string;
    shippingDetailsCity: string;
    shippingDetailsPinCode: string;
    shippingDetailsPhoneNumber: string;
    isBillingSameAsShipping: boolean;
    termsAndConditions?: Array<string>;
    contactNumber: string;
    emailId: string;
    incoTerms: string;
    termsOfPayment: string;
    customerReferenceNumber: string;
    customerReferenceDate: string;
    consigneeShipTo: string;
    buyerBillTo: string;
    freightCharges?: number | null;
    items: Array<LegacyQuotationWriteLineItem>;
    shareWithCustomer?: boolean;
};

export type PlutoQuotationLineItemRequest = {
    compatibleMachines?: Array<string>;
    discountPercent?: number | null;
    isCustom: boolean;
    marketPrice?: number | null;
    productCatalogueId?: Uuid | null;
    productNameOverride?: string | null;
    quantity: number;
    requestedPrice: number;
    skuOverride?: string | null;
    sortOrder?: number | null;
};

export function parsePlutoMoneyAmount(value: string | null | undefined): number {
    if (value === null || value === undefined) {
        return 0;
    }
    const parsed = Number(value);
    if (Number.isFinite(parsed) !== true) {
        return 0;
    }
    return parsed;
}

export function mapPlutoQuotationStatusToAdminStatus(plutoStatus: string): Uuid {
    if (plutoStatus === PLUTO_QUOTATION_STATUS_APPROVED || plutoStatus === PLUTO_QUOTATION_STATUS_CONVERTED_TO_CART) {
        return QuotationStatus.Sent;
    }
    if (plutoStatus === PLUTO_QUOTATION_STATUS_EXPIRED) {
        return QuotationStatus.Expired;
    }
    return QuotationStatus.Requested;
}

function buildAddressSnapshotFromLegacyPayload({
    address,
    city,
    companyName,
    email,
    gstin,
    phoneNumber,
    pinCode,
    state,
}: {
    address: string;
    city: string;
    companyName: string;
    email: string;
    gstin?: string | null;
    phoneNumber: string;
    pinCode: string;
    state: string;
}): PlutoAddressSnapshot {
    return {
        address: address,
        city: city,
        companyName: companyName,
        email: email,
        gstin: gstin ?? null,
        phoneNumber: phoneNumber,
        pinCode: pinCode,
        state: state,
    };
}

export function buildPlutoAdminCommercialDetailsFromLegacyPayload(
    payload: LegacyQuotationWritePayload,
    options?: {
        isAdvanced?: boolean;
    },
): PlutoAdminCommercialDetails {
    return {
        billingDetails: buildAddressSnapshotFromLegacyPayload({
            address: payload.billingDetailsAddress,
            city: payload.billingDetailsCity,
            companyName: payload.billingDetailsCompanyName,
            email: payload.billingDetailsEmail,
            gstin: payload.billingDetailsGstin,
            phoneNumber: payload.billingDetailsPhoneNumber,
            pinCode: payload.billingDetailsPinCode,
            state: payload.billingDetailsState,
        }),
        buyerBillTo: payload.buyerBillTo,
        consigneeShipTo: payload.consigneeShipTo,
        contactNumber: payload.contactNumber,
        customerReferenceDate: payload.customerReferenceDate,
        customerReferenceNumber: payload.customerReferenceNumber,
        discountType: payload.discountType ?? null,
        discountValue: payload.discountValue ?? null,
        emailId: payload.emailId,
        freightCharges: payload.freightCharges ?? null,
        incoTerms: payload.incoTerms,
        isBillingSameAsShipping: payload.isBillingSameAsShipping,
        isAdvanced: options?.isAdvanced === true,
        shippingDetails: buildAddressSnapshotFromLegacyPayload({
            address: payload.shippingDetailsAddress,
            city: payload.shippingDetailsCity,
            companyName: payload.shippingDetailsCompanyName,
            email: payload.shippingDetailsEmail,
            gstin: payload.shippingDetailsGstin,
            phoneNumber: payload.shippingDetailsPhoneNumber,
            pinCode: payload.shippingDetailsPinCode,
            state: payload.shippingDetailsState,
        }),
        taxRate: payload.taxRate,
        termsAndConditions: payload.termsAndConditions ?? [],
        termsOfPayment: payload.termsOfPayment,
    };
}

function nonBlankOrNull(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return null;
    }
    return trimmed;
}

function mapLegacyLineItemToPlutoLineItem(
    lineItem: LegacyQuotationWriteLineItem,
    options?: {
        includeCatalogDisplayFields?: boolean;
    },
): PlutoQuotationLineItemRequest {
    const isCustom = lineItem.isCustom === true;
    const includeCatalogDisplayFields = options?.includeCatalogDisplayFields === true;
    const shouldSendDisplayFields = isCustom === true || includeCatalogDisplayFields === true;
    return {
        compatibleMachines: lineItem.compatibleMachines ?? [],
        discountPercent: lineItem.discount ?? null,
        isCustom: isCustom,
        marketPrice: lineItem.marketPrice,
        productCatalogueId: lineItem.variantId ?? null,
        productNameOverride: shouldSendDisplayFields === true ? nonBlankOrNull(lineItem.productName) : null,
        quantity: lineItem.quantity,
        requestedPrice: lineItem.sellingPrice,
        skuOverride: shouldSendDisplayFields === true ? nonBlankOrNull(lineItem.sku) : null,
        sortOrder: lineItem.sortOrder ?? null,
    };
}

export function deriveSendToCustomerFromLegacyPayload(payload: LegacyQuotationWritePayload): boolean {
    if (payload.shareWithCustomer === true) {
        return true;
    }
    return payload.status === QuotationStatus.Sent;
}

export function buildPlutoCreateBodyFromLegacyPayload(
    payload: LegacyQuotationWritePayload,
    options?: {
        includeCatalogDisplayFields?: boolean;
        isAdvanced?: boolean;
    },
): {
    adminCommercialDetails: PlutoAdminCommercialDetails;
    items: Array<PlutoQuotationLineItemRequest>;
    ottokartStorefrontCustomerId: Uuid;
    sendToCustomer: boolean;
} {
    return {
        adminCommercialDetails: buildPlutoAdminCommercialDetailsFromLegacyPayload(payload, {
            isAdvanced: options?.isAdvanced === true,
        }),
        items: payload.items.map((lineItem) => {
            return mapLegacyLineItemToPlutoLineItem(lineItem, options);
        }),
        ottokartStorefrontCustomerId: payload.customerId,
        sendToCustomer: deriveSendToCustomerFromLegacyPayload(payload),
    };
}

export function buildPlutoReviseBodyFromLegacyPayload(
    payload: LegacyQuotationWritePayload,
    options?: {
        isAdvanced?: boolean;
    },
): {
    adminCommercialDetails: PlutoAdminCommercialDetails;
    items: Array<PlutoQuotationLineItemRequest>;
    ottokartStorefrontCustomerId: Uuid;
    sendToCustomer: boolean;
    sourceQuotationId: Uuid;
} {
    if (payload.quotationId === undefined) {
        throw new Error("sourceQuotationId is required to revise a quotation");
    }
    return {
        ...buildPlutoCreateBodyFromLegacyPayload(payload, options),
        sourceQuotationId: payload.quotationId,
    };
}

function applyAdminCommercialDetailsToQuotationFields({
    adminCommercialDetails,
    plutoQuotation,
}: {
    adminCommercialDetails: PlutoAdminCommercialDetails | null;
    plutoQuotation: PlutoQuotation;
}): Pick<
    Quotation,
    | "billingDetailsCompanyName"
    | "billingDetailsGstin"
    | "billingDetailsPhoneNumber"
    | "billingDetailsEmail"
    | "billingDetailsAddress"
    | "billingDetailsState"
    | "billingDetailsCity"
    | "billingDetailsPinCode"
    | "shippingDetailsCompanyName"
    | "shippingDetailsGstin"
    | "shippingDetailsPhoneNumber"
    | "shippingDetailsEmail"
    | "shippingDetailsAddress"
    | "shippingDetailsState"
    | "shippingDetailsCity"
    | "shippingDetailsPinCode"
    | "isBillingSameAsShipping"
    | "discountType"
    | "discountValue"
    | "taxRate"
    | "termsAndConditions"
    | "contactNumber"
    | "emailId"
    | "incoTerms"
    | "termsOfPayment"
    | "customerReferenceNumber"
    | "customerReferenceDate"
    | "consigneeShipTo"
    | "buyerBillTo"
    | "freightCharges"
> {
    if (adminCommercialDetails === null) {
        return {
            billingDetailsCompanyName: PLUTO_QUOTATION_EMPTY_FIELD,
            billingDetailsGstin: null,
            billingDetailsPhoneNumber: PLUTO_QUOTATION_EMPTY_FIELD,
            billingDetailsEmail: PLUTO_QUOTATION_EMPTY_FIELD,
            billingDetailsAddress: PLUTO_QUOTATION_EMPTY_FIELD,
            billingDetailsState: PLUTO_QUOTATION_EMPTY_FIELD,
            billingDetailsCity: PLUTO_QUOTATION_EMPTY_FIELD,
            billingDetailsPinCode: PLUTO_QUOTATION_EMPTY_FIELD,
            shippingDetailsCompanyName: PLUTO_QUOTATION_EMPTY_FIELD,
            shippingDetailsGstin: null,
            shippingDetailsPhoneNumber: PLUTO_QUOTATION_EMPTY_FIELD,
            shippingDetailsEmail: PLUTO_QUOTATION_EMPTY_FIELD,
            shippingDetailsAddress: PLUTO_QUOTATION_EMPTY_FIELD,
            shippingDetailsState: PLUTO_QUOTATION_EMPTY_FIELD,
            shippingDetailsCity: PLUTO_QUOTATION_EMPTY_FIELD,
            shippingDetailsPinCode: PLUTO_QUOTATION_EMPTY_FIELD,
            isBillingSameAsShipping: true,
            discountType: null,
            discountValue: null,
            taxRate: 0,
            termsAndConditions: null,
            contactNumber: PLUTO_QUOTATION_EMPTY_FIELD,
            emailId: PLUTO_QUOTATION_EMPTY_FIELD,
            incoTerms: PLUTO_QUOTATION_EMPTY_FIELD,
            termsOfPayment: PLUTO_QUOTATION_EMPTY_FIELD,
            customerReferenceNumber: PLUTO_QUOTATION_EMPTY_FIELD,
            customerReferenceDate: plutoQuotation.createdAt.slice(0, 10),
            consigneeShipTo: PLUTO_QUOTATION_EMPTY_FIELD,
            buyerBillTo: PLUTO_QUOTATION_EMPTY_FIELD,
            freightCharges: null,
        };
    }

    return {
        billingDetailsCompanyName: adminCommercialDetails.billingDetails.companyName,
        billingDetailsGstin: adminCommercialDetails.billingDetails.gstin,
        billingDetailsPhoneNumber: adminCommercialDetails.billingDetails.phoneNumber,
        billingDetailsEmail: adminCommercialDetails.billingDetails.email,
        billingDetailsAddress: adminCommercialDetails.billingDetails.address,
        billingDetailsState: adminCommercialDetails.billingDetails.state,
        billingDetailsCity: adminCommercialDetails.billingDetails.city,
        billingDetailsPinCode: adminCommercialDetails.billingDetails.pinCode,
        shippingDetailsCompanyName: adminCommercialDetails.shippingDetails.companyName,
        shippingDetailsGstin: adminCommercialDetails.shippingDetails.gstin,
        shippingDetailsPhoneNumber: adminCommercialDetails.shippingDetails.phoneNumber,
        shippingDetailsEmail: adminCommercialDetails.shippingDetails.email,
        shippingDetailsAddress: adminCommercialDetails.shippingDetails.address,
        shippingDetailsState: adminCommercialDetails.shippingDetails.state,
        shippingDetailsCity: adminCommercialDetails.shippingDetails.city,
        shippingDetailsPinCode: adminCommercialDetails.shippingDetails.pinCode,
        isBillingSameAsShipping: adminCommercialDetails.isBillingSameAsShipping,
        discountType: adminCommercialDetails.discountType,
        discountValue: adminCommercialDetails.discountValue,
        taxRate: adminCommercialDetails.taxRate,
        termsAndConditions: adminCommercialDetails.termsAndConditions,
        contactNumber: adminCommercialDetails.contactNumber,
        emailId: adminCommercialDetails.emailId,
        incoTerms: adminCommercialDetails.incoTerms,
        termsOfPayment: adminCommercialDetails.termsOfPayment,
        customerReferenceNumber: adminCommercialDetails.customerReferenceNumber,
        customerReferenceDate: adminCommercialDetails.customerReferenceDate,
        consigneeShipTo: adminCommercialDetails.consigneeShipTo,
        buyerBillTo: adminCommercialDetails.buyerBillTo,
        freightCharges: adminCommercialDetails.freightCharges,
    };
}

export function mapPlutoLineItemToQuotationItem({
    lineItem,
    plutoQuotation,
}: {
    lineItem: PlutoQuotationLineItem;
    plutoQuotation: PlutoQuotation;
}): QuotationItem | null {
    if (lineItem.isCustom === true || lineItem.productCatalogueId === null) {
        return null;
    }

    const unitPrice = parsePlutoMoneyAmount(lineItem.approvedPrice ?? lineItem.requestedPrice);
    const discountPercent = lineItem.discountPercent === null ? null : parsePlutoMoneyAmount(lineItem.discountPercent);

    return {
        id: lineItem.id,
        createdAt: lineItem.createdAt,
        updatedAt: lineItem.updatedAt,
        createdBy: STOREFRONT_SYSTEM_ACTOR_ID,
        updatedBy: STOREFRONT_SYSTEM_ACTOR_ID,
        quotationId: plutoQuotation.id,
        variantId: lineItem.productCatalogueId,
        sku: lineItem.skuOverride,
        sellingPrice: unitPrice,
        marketPrice: parsePlutoMoneyAmount(lineItem.marketPrice ?? lineItem.requestedPrice),
        quantity: lineItem.quantity,
        discount: discountPercent === 0 ? null : discountPercent,
        compatibleMachines: lineItem.compatibleMachines,
    };
}

export function mapPlutoLineItemToAdvancedQuotationItem({
    lineItem,
    plutoQuotation,
}: {
    lineItem: PlutoQuotationLineItem;
    plutoQuotation: PlutoQuotation;
}): AdvancedQuotationItem {
    const unitPrice = parsePlutoMoneyAmount(lineItem.approvedPrice ?? lineItem.requestedPrice);
    const discountPercent = lineItem.discountPercent === null ? null : parsePlutoMoneyAmount(lineItem.discountPercent);

    return {
        id: lineItem.id,
        createdAt: lineItem.createdAt,
        updatedAt: lineItem.updatedAt,
        createdBy: STOREFRONT_SYSTEM_ACTOR_ID,
        updatedBy: STOREFRONT_SYSTEM_ACTOR_ID,
        quotationId: plutoQuotation.id,
        variantId: lineItem.productCatalogueId,
        productName: lineItem.productNameOverride ?? "",
        categoryName: null,
        sku: lineItem.skuOverride,
        sortOrder: lineItem.sortOrder ?? 0,
        isCustom: lineItem.isCustom,
        sellingPrice: unitPrice,
        marketPrice: parsePlutoMoneyAmount(lineItem.marketPrice ?? lineItem.requestedPrice),
        quantity: lineItem.quantity,
        discount: discountPercent === 0 ? null : discountPercent,
        compatibleMachines: lineItem.compatibleMachines,
    };
}

function buildSyntheticQuotationItems(plutoQuotation: PlutoQuotation): Array<QuotationItem> {
    const totalAmount = parsePlutoMoneyAmount(plutoQuotation.totalAmount);
    if (totalAmount <= 0) {
        return [];
    }

    return [
        {
            id: plutoQuotation.revisionId,
            createdAt: plutoQuotation.createdAt,
            updatedAt: plutoQuotation.updatedAt,
            createdBy: STOREFRONT_SYSTEM_ACTOR_ID,
            updatedBy: STOREFRONT_SYSTEM_ACTOR_ID,
            quotationId: plutoQuotation.id,
            variantId: plutoQuotation.id,
            sku: null,
            sellingPrice: totalAmount,
            marketPrice: totalAmount,
            quantity: 1,
            discount: null,
            compatibleMachines: [],
        },
    ];
}

function mapPlutoLineItemsToQuotationItems({
    lineItems,
    plutoQuotation,
}: {
    lineItems: Array<PlutoQuotationLineItem>;
    plutoQuotation: PlutoQuotation;
}): Array<QuotationItem> {
    const mappedItems = lineItems
        .map((lineItem) => {
            return mapPlutoLineItemToQuotationItem({
                lineItem: lineItem,
                plutoQuotation: plutoQuotation,
            });
        })
        .filter((lineItem): lineItem is QuotationItem => lineItem !== null);

    if (mappedItems.length > 0) {
        return mappedItems;
    }

    return buildSyntheticQuotationItems(plutoQuotation);
}

function mapPlutoLineItemsToAdvancedQuotationItems({
    lineItems,
    plutoQuotation,
}: {
    lineItems: Array<PlutoQuotationLineItem>;
    plutoQuotation: PlutoQuotation;
}): Array<AdvancedQuotationItem> {
    if (lineItems.length > 0) {
        return lineItems.map((lineItem) => {
            return mapPlutoLineItemToAdvancedQuotationItem({
                lineItem: lineItem,
                plutoQuotation: plutoQuotation,
            });
        });
    }

    return buildSyntheticQuotationItems(plutoQuotation).map((item) => {
        return {
            ...item,
            variantId: item.variantId,
            productName: PLUTO_QUOTATION_EMPTY_FIELD,
            categoryName: null,
            sku: null,
            sortOrder: 0,
            isCustom: false,
        };
    });
}

function buildBaseQuotationFromPluto({
    lineItems,
    plutoQuotation,
}: {
    lineItems?: Array<PlutoQuotationLineItem>;
    plutoQuotation: PlutoQuotation;
}): Quotation {
    const commercialFields = applyAdminCommercialDetailsToQuotationFields({
        adminCommercialDetails: plutoQuotation.adminCommercialDetails,
        plutoQuotation: plutoQuotation,
    });

    return {
        id: plutoQuotation.id,
        createdAt: plutoQuotation.createdAt,
        updatedAt: plutoQuotation.updatedAt,
        createdBy: STOREFRONT_SYSTEM_ACTOR_ID,
        updatedBy: STOREFRONT_SYSTEM_ACTOR_ID,
        generatedBy: plutoQuotation.generatedBy,
        generatedByEmail: null,
        sharedBy: plutoQuotation.sharedBy,
        sharedByEmail: null,
        slug: plutoQuotation.quoteNumber,
        customerId: plutoQuotation.ottokartStorefrontCustomerId,
        status: mapPlutoQuotationStatusToAdminStatus(plutoQuotation.status),
        plutoStatus: plutoQuotation.status,
        validUntil: plutoQuotation.validUntil,
        sourceQuotationId: plutoQuotation.sourceQuotationId ?? plutoQuotation.id,
        items:
            lineItems !== undefined
                ? mapPlutoLineItemsToQuotationItems({
                      lineItems: lineItems,
                      plutoQuotation: plutoQuotation,
                  })
                : buildSyntheticQuotationItems(plutoQuotation),
        ...commercialFields,
    };
}

/**
 * tuffex--admin stores standard RFQs / Quotations-module quotes in `quotations`
 * and Advanced quotes in `advanced_quotations`. Pluto uses one table, so we
 * split lists using the persisted flag (and line-item shape for legacy rows).
 */
export function isPlutoQuotationAdvanced({
    lineItems,
    plutoQuotation,
}: {
    lineItems: Array<PlutoQuotationLineItem>;
    plutoQuotation: PlutoQuotation;
}): boolean {
    const flagged = plutoQuotation.adminCommercialDetails?.isAdvanced;
    if (flagged === true) {
        return true;
    }
    if (flagged === false) {
        return false;
    }

    for (const lineItem of lineItems) {
        if (lineItem.isCustom === true) {
            return true;
        }
        if (lineItem.sortOrder !== null) {
            return true;
        }
        if (lineItem.productNameOverride !== null) {
            return true;
        }
    }
    return false;
}

export function filterPlutoQuotationsForModule<
    TRow extends {
        lineItems: Array<PlutoQuotationLineItem>;
        quotation: PlutoQuotation;
    },
>(
    rows: Array<TRow>,
    module: "advanced" | "standard",
): Array<TRow> {
    return rows.filter((row) => {
        const isAdvanced = isPlutoQuotationAdvanced({
            lineItems: row.lineItems,
            plutoQuotation: row.quotation,
        });
        if (module === "advanced") {
            return isAdvanced === true;
        }
        return isAdvanced !== true;
    });
}

export function mapPlutoQuotationToAdminQuotation({
    lineItems,
    plutoQuotation,
}: {
    lineItems?: Array<PlutoQuotationLineItem>;
    plutoQuotation: PlutoQuotation;
}): Quotation {
    return buildBaseQuotationFromPluto({
        lineItems: lineItems,
        plutoQuotation: plutoQuotation,
    });
}

export function mapPlutoQuotationToAdvancedQuotation({
    lineItems,
    plutoQuotation,
}: {
    lineItems?: Array<PlutoQuotationLineItem>;
    plutoQuotation: PlutoQuotation;
}): AdvancedQuotation {
    const baseQuotation = buildBaseQuotationFromPluto({
        lineItems: lineItems,
        plutoQuotation: plutoQuotation,
    });

    return {
        ...baseQuotation,
        items:
            lineItems !== undefined
                ? mapPlutoLineItemsToAdvancedQuotationItems({
                      lineItems: lineItems,
                      plutoQuotation: plutoQuotation,
                  })
                : mapPlutoLineItemsToAdvancedQuotationItems({
                      lineItems: [],
                      plutoQuotation: plutoQuotation,
                  }),
    };
}
